import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';

import {
  createStripeCustomer,
  attachPaymentMethodToCustomer,
  setDefaultPaymentMethod
} from '../../../../../../Utilities/api';

import { WhiteBackButton } from '../../../../../../Utilities/components/Buttons';
import { StateContext } from '../../../../../../StateProvider';
import Footer from '../../../../../../Footer';
import './AddPaymentMethod.css';

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      padding: '15px',
      '::placeholder': { color: '#aab7c4' }
    },
    invalid: { color: '#9e2146' }
  }
};

export default function AddPaymentMethod() {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  const { user, refreshUserData, loading, setLoading } = useContext(StateContext);

  const [cardholderName, setCardholderName] = useState('');
  const [error, setError] = useState(null);

  function resolveErrorMessage(err) {
    if (
      err &&
      err.response &&
      err.response.data &&
      err.response.data.message
    ) {
      return err.response.data.message;
    }

    if (err && err.error && err.error.message) {
      return err.error.message;
    }

    if (err && err.message) {
      return err.message;
    }

    return 'Something went wrong. Please try a different card.';
  }

  async function handleAddPaymentMethod(e) {
    e.preventDefault();
    setError(null);

    if (!stripe || !elements) {
      setError('Payment fields are still loading. Please try again.');
      return;
    }

    setLoading(true);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card input failed to load.');
      setLoading(false);
      return;
    }

    try {
      // 1. Create Stripe payment method
      const result = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: { name: cardholderName }
      });

      if (result && result.error) {
        setError(result.error.message);
        setLoading(false);
        return;
      }

      const paymentMethod = result.paymentMethod;

      // 2. Ensure Stripe customer exists
      let stripeCustomerId = user.stripeCustomerId;
      if (!stripeCustomerId) {
        const res = await createStripeCustomer(user.uid, user.email);
        stripeCustomerId = res.stripeCustomerId;
      }

      // 3. Attach payment method
      const attachResponse = await attachPaymentMethodToCustomer(
        stripeCustomerId,
        paymentMethod.id,
        user.uid
      );

      // 4. Refresh user data
      await refreshUserData(user.uid);

      // 5. If first card, set default
      let updatedMethods = [];
      if (
        attachResponse &&
        attachResponse.paymentMethods &&
        Array.isArray(attachResponse.paymentMethods)
      ) {
        updatedMethods = attachResponse.paymentMethods;
      }

      if (updatedMethods.length === 1) {
        await setDefaultPaymentMethod(user.uid, paymentMethod.id);
        await refreshUserData(user.uid);
      }

      navigate(-1);

    } catch (err) {
      console.error('Add Payment Method error:', err);
      setError(resolveErrorMessage(err));
    }

    setLoading(false);
  }

  return (
    <>
      <div className="add-payment-backdrop">
        <div className="container">

          <div className="payment-add-button-container">
            <WhiteBackButton />
          </div>

          <div className="add-payment-method">
            <h2 className="add-payment-header">Add Payment Method</h2>

            <form>
              <label htmlFor="cardholderName">Cardholder Name</label>
              <input
                type="text"
                id="cardholderName"
                value={cardholderName}
                onChange={function (e) {
                  setCardholderName(e.target.value);
                  if (error) setError(null);
                }}
                placeholder="Enter Here"
                required
              />
            </form>

            <div className="add-payment-stripe-container">
              <label className="add-payment-stripe-label">Card Details</label>
              <CardElement
                className="StripeElement"
                options={cardElementOptions}
                onChange={function () {
                  if (error) setError(null);
                }}
              />
            </div>

            {error && (
              <p className="stripe-error-message">
                ⚠️ {error}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="add-payment-method-submit-container">
        <button
          onClick={handleAddPaymentMethod}
          className="add-payment-method-submit"
          disabled={loading}
        >
          {loading ? 'Processing…' : 'Add'}
        </button>
      </div>

      <Footer />
    </>
  );
}
